import { useEffect } from 'react'
import Lenis from 'lenis'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { prefersReducedMotion } from './animation-config'

gsap.registerPlugin(ScrollTrigger)

let lenisInstance: Lenis | null = null

/**
 * Initialize Lenis smooth scrolling with GSAP ScrollTrigger integration
 * Adapted for single-page app (no React Router)
 */
export function useLenis() {
  useEffect(() => {
    // Skip for reduced motion preference
    if (prefersReducedMotion()) {
      return
    }

    // Only create one instance
    if (lenisInstance) {
      return
    }

    lenisInstance = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      orientation: 'vertical',
      gestureOrientation: 'vertical',
      smoothWheel: true,
      touchMultiplier: 2,
    })

    // Connect Lenis scroll to ScrollTrigger
    lenisInstance.on('scroll', ScrollTrigger.update)

    // Mark GSAP as ready so CSS knows to let GSAP control animations
    requestAnimationFrame(() => {
      document.documentElement.classList.add('gsap-ready')
      ScrollTrigger.refresh()
    })

    // Integrate with GSAP ticker for consistent timing
    gsap.ticker.add((time) => {
      lenisInstance?.raf(time * 1000)
    })

    // Disable GSAP's lagSmoothing for Lenis compatibility
    gsap.ticker.lagSmoothing(0)

    return () => {
      if (lenisInstance) {
        document.documentElement.classList.remove('gsap-ready')
        lenisInstance.off('scroll', ScrollTrigger.update)
        gsap.ticker.remove(lenisInstance.raf)
        lenisInstance.destroy()
        lenisInstance = null
      }
    }
  }, [])

  return lenisInstance
}

/**
 * Get the global Lenis instance (for programmatic scrolling)
 */
export function getLenis(): Lenis | null {
  return lenisInstance
}
